import { kanbanStageAutomationsAPI } from '../../api/kanban';
import { kanbanErrorMessage } from './kanbanApiError';

/**
 * Automações de estágio do Kanban — recurso REST de verdade.
 *
 * Substitui o `kanbanStageAutomationsMock`, que era 100% memória (o cabeçalho
 * dele dizia "nada vai pro backend"). Aqui cada mutação de estado só acontece
 * DEPOIS da resposta do servidor: automação que não gravou não pode aparecer
 * na tela como se tivesse gravado.
 *
 * `byStageId[stageId]` ausente ≠ estágio sem automação. Ausente é "ainda não
 * sei" (nunca busquei, ou a busca falhou); a lista vazia é um array vazio.
 * Os getters preservam essa diferença — quem renderiza precisa dela pra não
 * desenhar "nenhuma automação" em cima de um GET que quebrou.
 */
export const state = {
  byStageId: {},
  errorByStageId: {},
  uiFlags: {
    isFetching: false,
    isSaving: false,
    isDeleting: false,
  },
};

export const getters = {
  // Array quando carregado, null quando desconhecido (não buscado / erro).
  forStage: _state => stageId => _state.byStageId[stageId] ?? null,
  // Idem: null = desconhecido. Contador não inventa zero.
  countForStage: _state => stageId => _state.byStageId[stageId]?.length ?? null,
  errorForStage: _state => stageId => _state.errorByStageId[stageId] || null,
  isLoadedForStage: _state => stageId =>
    Array.isArray(_state.byStageId[stageId]),
  uiFlags: _state => _state.uiFlags,
};

// O 422 do controller vem como { error: "ação 2 (assign_team): falta team_id" }.
// É o texto que nomeia O QUE está errado — quem chama mostra ele, não um
// "erro ao salvar" genérico. A regra é a MESMA de funil, fase e passagem, então
// mora em `kanbanApiError`; aqui fica só o nome de domínio que os consumidores
// deste módulo já importam.
export const automationErrorMessage = kanbanErrorMessage;

export const actions = {
  async fetch({ commit }, { pipelineId, stageId }) {
    commit('SET_UI_FLAG', { isFetching: true });
    try {
      const { data } = await kanbanStageAutomationsAPI.list(
        pipelineId,
        stageId
      );
      commit('SET_FOR_STAGE', { stageId, automations: data.payload || [] });
      return data.payload || [];
    } catch (error) {
      // Marca o estágio como "não sei" e propaga: a tela mostra o erro em vez
      // de uma lista vazia mentindo que o estágio não tem automação.
      commit('SET_ERROR_FOR_STAGE', {
        stageId,
        message: automationErrorMessage(error),
      });
      throw error;
    } finally {
      commit('SET_UI_FLAG', { isFetching: false });
    }
  },

  // Cache com releitura em segundo plano. Dois donos de tela (o contador do
  // StageForm e a lista do drawer) pedem a mesma coisa ao abrir; a primeira
  // abertura espera a request, as seguintes rendem o que está em mão e releem
  // por trás. Só cache, como era, deixava o drawer mentindo: abriu a fase uma
  // vez vazia, outro admin (ou o console) criou a regra, o selo da coluna
  // mostrava 1 e o drawer seguia "nenhuma automação" até dar F5.
  ensureFetched({ dispatch, state: s }, { pipelineId, stageId }) {
    const cached = s.byStageId[stageId];
    if (Array.isArray(cached)) {
      dispatch('fetch', { pipelineId, stageId }).catch(() => {
        // Erro da releitura já vai pro state (loadError) e a lista mostra.
      });
      return Promise.resolve(cached);
    }
    return dispatch('fetch', { pipelineId, stageId });
  },

  async create({ commit }, { pipelineId, stageId, payload }) {
    commit('SET_UI_FLAG', { isSaving: true });
    try {
      const { data } = await kanbanStageAutomationsAPI.create(
        pipelineId,
        stageId,
        payload
      );
      commit('UPSERT_FOR_STAGE', { stageId, automation: data });
      return data;
    } finally {
      commit('SET_UI_FLAG', { isSaving: false });
    }
  },

  async update({ commit }, { pipelineId, stageId, id, payload }) {
    commit('SET_UI_FLAG', { isSaving: true });
    try {
      const { data } = await kanbanStageAutomationsAPI.update(
        pipelineId,
        stageId,
        id,
        payload
      );
      commit('UPSERT_FOR_STAGE', { stageId, automation: data });
      return data;
    } finally {
      commit('SET_UI_FLAG', { isSaving: false });
    }
  },

  async delete({ commit }, { pipelineId, stageId, id }) {
    commit('SET_UI_FLAG', { isDeleting: true });
    try {
      await kanbanStageAutomationsAPI.delete(pipelineId, stageId, id);
      commit('REMOVE_FOR_STAGE', { stageId, id });
    } finally {
      commit('SET_UI_FLAG', { isDeleting: false });
    }
  },

  // Ligar/desligar é um PATCH só de `active`: o controller não mexe em
  // `actions` quando a chave não vem no payload.
  toggleActive({ dispatch }, { pipelineId, stageId, automation }) {
    return dispatch('update', {
      pipelineId,
      stageId,
      id: automation.id,
      payload: { active: !automation.active },
    });
  },
};

const withoutKey = (map, key) => {
  const next = { ...map };
  delete next[key];
  return next;
};

export const mutations = {
  SET_UI_FLAG(_state, flags) {
    _state.uiFlags = { ..._state.uiFlags, ...flags };
  },
  SET_FOR_STAGE(_state, { stageId, automations }) {
    _state.byStageId = { ..._state.byStageId, [stageId]: [...automations] };
    _state.errorByStageId = withoutKey(_state.errorByStageId, stageId);
  },
  SET_ERROR_FOR_STAGE(_state, { stageId, message }) {
    _state.byStageId = withoutKey(_state.byStageId, stageId);
    _state.errorByStageId = { ..._state.errorByStageId, [stageId]: message };
  },
  UPSERT_FOR_STAGE(_state, { stageId, automation }) {
    const current = _state.byStageId[stageId] || [];
    const index = current.findIndex(a => a.id === automation.id);
    const next =
      index >= 0
        ? [...current.slice(0, index), automation, ...current.slice(index + 1)]
        : [...current, automation];
    _state.byStageId = { ..._state.byStageId, [stageId]: next };
  },
  REMOVE_FOR_STAGE(_state, { stageId, id }) {
    const current = _state.byStageId[stageId];
    if (!Array.isArray(current)) return;
    _state.byStageId = {
      ..._state.byStageId,
      [stageId]: current.filter(a => a.id !== id),
    };
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
